import { useState, useEffect } from "react";
import { authAxios } from "../utils/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Lock, Loader2, ChevronDown, ChevronUp, Target, X } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";

// Stage labels and objectives (max ~50 words each)
const STAGE_LABELS = {
  1: "Spark", 2: "Opening", 3: "Resonance", 4: "Safety", 5: "Curiosity",
  6: "Attraction", 7: "Vulnerability", 8: "Alignment", 9: "Momentum",
  10: "Intimacy", 11: "Integration", 12: "Commitment", 13: "Union"
};

const STAGE_OBJECTIVES = {
  1: "Make a memorable first impression. Be approachable and authentic to create that initial spark of interest.",
  2: "Start a genuine conversation. Establish your presence and build connection through engaging dialogue.",
  3: "Find common ground. Discover shared interests and values to deepen the connection naturally.",
  4: "Create emotional safety. Make them feel comfortable opening up without judgment or pressure.",
  5: "Cultivate mutual curiosity. Ask meaningful questions and show genuine interest in their world.",
  6: "Let chemistry build naturally. Add playful flirting and let attraction develop organically.",
  7: "Share vulnerabilities. Create space for authentic emotional intimacy and deeper truths.",
  8: "Explore alignment. Discuss values and life goals to ensure compatible directions.",
  9: "Build consistent momentum. Keep connection strong through regular, quality interactions.",
  10: "Deepen intimacy. Navigate emotional and physical closeness with care and consent.",
  11: "Integrate lives. Meet important people and share meaningful experiences together.",
  12: "Establish commitment. Have honest conversations about relationship status and exclusivity.",
  13: "Nurture partnership. Continue growing together while maintaining individual identities."
};

// Detailed descriptions with examples (~500 words each)
const STAGE_DETAILED = {
  1: {
    title: "Spark - Making That First Impression",
    description: `The Spark stage is all about creating that initial moment of connection. This is where first impressions are formed, and your goal is to stand out in a positive, memorable way while remaining authentic to who you are.

At this stage, the persona is cautious and evaluating. They're assessing whether you're worth their time and attention. Your messages should be warm but not overwhelming, interesting but not try-hard. Think of it as introducing yourself at a party – you want to be approachable and genuine.

**Key Principles:**
• Lead with curiosity, not intensity
• Be authentic – don't perform a version of yourself
• Keep messages relatively brief but thoughtful
• Show you've noticed something specific about them
• Avoid generic openers or pickup lines

**What Works Well:**
• Commenting on something specific from their profile
• Asking a genuine question that shows interest
• Light humor that feels natural, not forced
• Sharing a brief, relevant observation about yourself

**What to Avoid:**
• Overly long introductory messages
• Compliments that feel generic or excessive
• Questions that are too personal too soon
• Being negative or self-deprecating
• Rushing to suggest meeting up`,
    examples: [
      { good: true, text: "I noticed you're into hiking – that trail photo looks amazing! Is that a local spot or somewhere you traveled to?" },
      { good: true, text: "Your taste in books caught my attention. I just finished something similar. What drew you to that genre?" },
      { good: false, text: "Hey beautiful, you're the most gorgeous person I've seen on here 😍" },
      { good: false, text: "Hi. How are you? What are you looking for on here?" }
    ]
  },
  2: {
    title: "Opening - Building the Conversation",
    description: `The Opening stage is where you transition from a simple introduction to an actual conversation. You've caught their attention – now it's time to build momentum and establish yourself as someone worth talking to.

At this level, the persona is more receptive but still guarded. They're looking for signs that you're genuinely interested in them as a person, not just going through the motions. Your goal is to create a natural back-and-forth flow.

**Key Principles:**
• Build on what they share – show you're listening
• Balance questions with sharing about yourself
• Find threads of connection to explore
• Keep the energy positive and engaging
• Let the conversation breathe – don't interrogate

**What Works Well:**
• Following up on details they mention
• Sharing relevant stories or experiences
• Finding humor in shared observations
• Asking "how" and "why" questions, not just "what"
• Showing genuine reactions to what they share

**What to Avoid:**
• Rapid-fire questions without engagement
• One-word responses or low-effort replies
• Changing topics abruptly
• Making everything about yourself
• Being too eager or available`,
    examples: [
      { good: true, text: "That coffee shop sounds perfect! I'm always hunting for good spots to work from. What makes their flat white so special?" },
      { good: true, text: "Ha! I had the exact same experience with that show – couldn't stop watching. Did you end up finishing it in one weekend too?" },
      { good: false, text: "Cool. So what do you do for work? Where did you grow up? Any siblings?" },
      { good: false, text: "Nice" }
    ]
  },
  3: {
    title: "Resonance - Finding Common Ground",
    description: `Resonance is the stage where real connection begins to form. You've established a conversation, and now you're discovering the things that make you click – shared interests, similar values, or complementary perspectives.

The persona is warming up to you. They're starting to see you as someone they might genuinely enjoy spending time with. Your goal is to deepen the conversation by exploring what you have in common while staying curious about your differences.

**Key Principles:**
• Celebrate shared interests authentically
• Explore topics with genuine depth
• Share your own perspectives and opinions
• Create inside jokes or references
• Build on earlier conversations

**What Works Well:**
• Enthusiastic responses to shared interests
• Deeper dives into topics you both enjoy
• Playful debates about preferences
• Remembering and referencing earlier details
• Suggesting you'd enjoy doing something related

**What to Avoid:**
• Pretending to like things you don't
• Agreeing with everything they say
• Surface-level responses to meaningful shares
• Forgetting what they've told you
• Rushing past connection moments`,
    examples: [
      { good: true, text: "Wait, you've been to that festival too?! Which year? I feel like we might have crossed paths without knowing it." },
      { good: true, text: "I love how passionate you are about this. Okay, controversial opinion – I actually think the sequel was better. Fight me 😄" },
      { good: false, text: "Oh yeah, I love that too — when you clearly don't" },
      { good: false, text: "That's cool I guess. Anyway, what else do you like?" }
    ]
  },
  4: {
    title: "Safety - Building Trust",
    description: `The Safety stage is about creating emotional security. By now, you've established rapport – but for the connection to deepen, the other person needs to feel they can trust you with more of themselves.

At this level, the persona is deciding whether you're someone they can be vulnerable with. They're looking for consistency, respect for boundaries, and signs that you won't judge them. Your role is to be a safe harbor in the conversation.

**Key Principles:**
• Be consistent in your communication style
• Respect boundaries without being asked
• Validate their feelings and experiences
• Share your own minor vulnerabilities
• Show emotional intelligence

**What Works Well:**
• Thoughtful responses to serious topics
• Acknowledging when they share something personal
• Being honest about your own feelings
• Checking in on how they're doing
• Giving space when they need it

**What to Avoid:**
• Pushing for information they're not offering
• Making jokes about sensitive topics
• Being inconsistent with your attention
• Dismissing their concerns or feelings
• Oversharing too quickly`,
    examples: [
      { good: true, text: "That sounds like it was really tough. Thanks for sharing that with me – it takes courage to open up about stuff like that." },
      { good: true, text: "Hey, no pressure at all if you're not ready to talk about that. I'm here whenever you want to." },
      { good: false, text: "That's nothing, wait till you hear what happened to me..." },
      { good: false, text: "Come on, you can tell me. Don't be so secretive." }
    ]
  },
  5: {
    title: "Curiosity - Deeper Discovery",
    description: `Curiosity is the stage of genuine exploration. You've built trust, and now there's space for deeper questions and more meaningful exchanges. This is where you start understanding who they really are.

The persona is more open now, willing to share thoughts, dreams, and experiences they wouldn't share with just anyone. Your curiosity should feel genuine and caring – not like an interview or investigation.

**Key Principles:**
• Ask questions that show you care about their inner world
• Listen actively and remember details
• Share your own depths in return
• Explore dreams, fears, and aspirations
• Be patient – depth takes time

**What Works Well:**
• "What" and "why" questions about meaningful topics
• Following up on things they mentioned before
• Sharing your own reflections and growth
• Discussing values, not just interests
• Wondering about their experiences together

**What to Avoid:**
• Rapid questioning without absorption
• Making assumptions about their answers
• Keeping things superficial when they go deep
• Forgetting important things they've shared
• Being judgmental about their views`,
    examples: [
      { good: true, text: "I've been thinking about what you said about your career change. What finally gave you the courage to make that leap?" },
      { good: true, text: "That's such an interesting way to see it. I've never thought about it that way. Tell me more about how you came to that perspective." },
      { good: false, text: "So what's your 5-year plan? What about 10 years? Do you want kids?" },
      { good: false, text: "Yeah, cool. What else?" }
    ]
  },
  6: {
    title: "Attraction - Chemistry Building",
    description: `The Attraction stage is where romantic and physical chemistry enters the conversation more explicitly. You've built a foundation of trust and mutual understanding – now there's space for flirtation, playfulness, and acknowledging the spark between you.

The persona is receptive to romantic attention at this point. They want to know you see them as more than a friend. Your flirting should feel natural, calibrated to their responses, and never crossing into discomfort.

**Key Principles:**
• Build on existing rapport, don't abandon it
• Calibrate flirting to their responses
• Keep playfulness balanced with substance
• Compliment specific things, not generic traits
• Create anticipation without pressure

**What Works Well:**
• Playful teasing that shows you pay attention
• Compliments about personality and choices
• Hints about wanting to spend time together
• Slightly more personal questions
• Building romantic tension naturally

**What to Avoid:**
• Sudden shift to heavy flirting
• Generic or physical-only compliments
• Ignoring signs they're uncomfortable
• Making everything sexual
• Abandoning the emotional connection`,
    examples: [
      { good: true, text: "I have to admit, every time you share one of your terrible puns, I like you a little more. What is wrong with me? 😂" },
      { good: true, text: "This conversation keeps making me smile. I'm starting to really look forward to your messages." },
      { good: false, text: "You're so hot. What are you wearing right now?" },
      { good: false, text: "We should definitely hook up sometime 😏" }
    ]
  },
  7: {
    title: "Vulnerability - Authentic Intimacy",
    description: `Vulnerability is the stage where real emotional intimacy develops. Both of you are willing to show the less polished parts of yourselves – fears, insecurities, past hurts, and authentic dreams.

The persona trusts you enough to be real. They're sharing things that feel risky, and they need to know you'll handle that with care. This is where connections transform from enjoyable to meaningful.

**Key Principles:**
• Honor vulnerability with presence and care
• Match their openness appropriately
• Never weaponize what they share
• Create space for difficult emotions
• Show your own authentic self

**What Works Well:**
• Thoughtful, unhurried responses to deep shares
• Sharing your own vulnerabilities in return
• Validating without trying to fix
• Remembering and checking in on hard things
• Being present without judgment

**What to Avoid:**
• Rushing past vulnerable moments
• One-upping their struggles
• Offering unsolicited advice
• Using their vulnerabilities against them
• Disappearing after they open up`,
    examples: [
      { good: true, text: "I really appreciate you trusting me with that. It actually helps me feel closer to you. I've struggled with something similar..." },
      { good: true, text: "That must have been incredibly hard. I'm here for you, and I'm not going anywhere." },
      { good: false, text: "That's rough. Anyway, did you see the game last night?" },
      { good: false, text: "You shouldn't feel that way. Just think positive!" }
    ]
  },
  8: {
    title: "Alignment - Values and Vision",
    description: `Alignment is about discovering whether your futures can merge. You're exploring not just whether you enjoy each other, but whether you want similar things from life – in relationships, career, family, lifestyle, and values.

The persona is evaluating long-term compatibility. They want to know if this connection has staying power. Honesty is crucial here – pretending to want what they want will only cause pain later.

**Key Principles:**
• Be honest about what you want from life
• Explore differences with curiosity, not judgment
• Look for compatibility, not carbon copies
• Discuss deal-breakers respectfully
• Envision possibilities together

**What Works Well:**
• Open discussions about future goals
• Honest sharing of values and priorities
• Exploring how your lives might fit together
• Respectful navigation of differences
• Dreaming together about possibilities

**What to Avoid:**
• Pretending to want things you don't
• Avoiding important compatibility topics
• Pressuring them to change their views
• Dismissing their goals as unimportant
• Making promises you can't keep`,
    examples: [
      { good: true, text: "I love that we both value adventure. I've been thinking about how we might create a life that includes travel and stability. What does your ideal balance look like?" },
      { good: true, text: "I want to be honest – I'm not sure I want kids. Is that something that's important to you? I'd rather talk about it now than later." },
      { good: false, text: "Oh yeah, I definitely want that too — when you don't" },
      { good: false, text: "Let's not talk about heavy stuff like the future. Let's just enjoy now." }
    ]
  },
  9: {
    title: "Momentum - Consistent Connection",
    description: `Momentum is about maintaining the energy and connection you've built. It's easy to let a great thing fade through neglect or inconsistency. This stage is about showing up regularly and keeping the relationship growing.

The persona needs to know you're invested. They're looking for reliability and continued effort. The honeymoon phase might be fading, but the connection should still feel prioritized.

**Key Principles:**
• Be consistent without being suffocating
• Keep creating new experiences together
• Maintain effort in your communication
• Address small issues before they grow
• Balance routine with novelty

**What Works Well:**
• Regular check-ins and thinking-of-you messages
• Planning future activities or dates
• Maintaining quality in conversations
• Showing interest in their daily life
• Surprising them occasionally

**What to Avoid:**
• Taking the connection for granted
• Becoming lazy with communication
• Only reaching out when you want something
• Letting long gaps happen without explanation
• Falling into boring routines`,
    examples: [
      { good: true, text: "Just saw something that reminded me of our conversation about travel. It made me smile. How's your Thursday going?" },
      { good: true, text: "I've been looking forward to seeing you all week. Already thinking about where we should explore next weekend." },
      { good: false, text: "Hey, haven't talked in a while. What's up? — after ghosting for days" },
      { good: false, text: "nm, u?" }
    ]
  },
  10: {
    title: "Intimacy - Deep Connection",
    description: `Intimacy is the stage where emotional and physical closeness intertwine fully. You know each other deeply, trust each other completely, and can be fully yourselves together. This is rare and precious.

The persona is all-in emotionally. They're looking for continued presence, ongoing respect, and the deepening of your bond. Physical intimacy, if present, should enhance rather than replace emotional connection.

**Key Principles:**
• Continue nurturing emotional intimacy
• Maintain respect and consent always
• Keep communicating openly about needs
• Celebrate your connection regularly
• Never stop being curious about them

**What Works Well:**
• Deep, meaningful conversations continue
• Physical affection matched with emotional care
• Ongoing vulnerability and trust-building
• Expressing appreciation and love
• Growing together through challenges

**What to Avoid:**
• Substituting physical for emotional intimacy
• Assuming you know everything about them
• Taking their presence for granted
• Stopping the communication of feelings
• Letting routine kill the spark`,
    examples: [
      { good: true, text: "Even after all this time, I keep discovering new things I love about you. Like how you always know exactly what to say." },
      { good: true, text: "I feel so safe with you. Thank you for always making space for me to be myself." },
      { good: false, text: "We don't need to talk about our feelings anymore. We already know each other." },
      { good: false, text: "Let's skip the talking and get to the good stuff." }
    ]
  },
  11: {
    title: "Integration - Merging Lives",
    description: `Integration is where two separate lives begin blending into a shared one. You're meeting important people in each other's lives, creating shared experiences, and building something together.

The persona is ready to include you in their broader world. They want to know you fit with their friends, family, and life – and they want to fit into yours. This is about building a partnership.

**Key Principles:**
• Be genuinely interested in their world
• Welcome them into yours authentically
• Navigate differences between your lives
• Build shared traditions and experiences
• Maintain individual identities too

**What Works Well:**
• Enthusiasm about meeting important people
• Making effort with their friends and family
• Creating new traditions together
• Balancing "us time" with other relationships
• Supporting each other's individual growth

**What to Avoid:**
• Resisting integration or staying separate
• Being dismissive of their important relationships
• Losing yourself completely in the merge
• Creating conflict between them and their people
• Rushing integration before they're ready`,
    examples: [
      { good: true, text: "I loved meeting your sister! I can see why you two are so close. Thanks for including me in your family dinner." },
      { good: true, text: "I'd really love for you to meet my best friend. You two would get along so well – same terrible sense of humor." },
      { good: false, text: "Do I really have to go to your friend's thing? Can't we just stay in?" },
      { good: false, text: "Your family is nice, but I'd rather just spend time with you alone." }
    ]
  },
  12: {
    title: "Commitment - Defining the Future",
    description: `Commitment is the stage where you explicitly define what you are to each other and what you're building together. It's about making promises, establishing exclusivity if desired, and choosing each other deliberately.

The persona wants clarity and security. They're ready to call this a relationship, to make plans, to commit. This requires honest conversation about where you both stand.

**Key Principles:**
• Have honest conversations about status
• Make deliberate choices about your future
• Honor the commitments you make
• Continue nurturing the relationship
• Face challenges as a team

**What Works Well:**
• Clear communication about exclusivity
• Making plans for the future together
• Showing up reliably for commitments
• Working through problems together
• Expressing commitment through actions

**What to Avoid:**
• Avoiding the "what are we" conversation
• Making commitments you won't keep
• Keeping options open while they commit
• Taking commitment for granted
• Letting commitment replace effort`,
    examples: [
      { good: true, text: "I've been thinking a lot about us. I know what I want – I want this to be official. I want you to be my person. How do you feel about that?" },
      { good: true, text: "I'm not interested in anyone else. You're who I want to build something with. I'm all in." },
      { good: false, text: "Let's just see where things go. Why do we need labels? — when they want clarity" },
      { good: false, text: "I mean, I guess we're exclusive... unless something better comes along." }
    ]
  },
  13: {
    title: "Union - Deep Partnership",
    description: `Union is the highest level – a deep, mature partnership where two people have truly become a team. You've weathered challenges, grown together, and built something meaningful. The work isn't over, but the foundation is strong.

The persona is your partner in every sense. They want continued growth, maintained connection, and a lifetime of building together. This is about nurturing what you've created.

**Key Principles:**
• Never stop dating each other
• Continue growing as individuals and partners
• Face life's challenges as a unified team
• Maintain gratitude and appreciation
• Keep the spark alive intentionally

**What Works Well:**
• Continued appreciation and romance
• Regular relationship check-ins
• Supporting each other's dreams
• Maintaining humor and playfulness
• Growing through challenges together

**What to Avoid:**
• Assuming the work is done
• Taking each other for granted
• Stopping the behaviors that got you here
• Letting outside stresses break your bond
• Forgetting to have fun together`,
    examples: [
      { good: true, text: "Even after all this time, I fall in love with you a little more every day. I'm so grateful we found each other." },
      { good: true, text: "We've been through so much together, and every challenge has made us stronger. I can't wait to see what we build next." },
      { good: false, text: "We don't need to do date nights anymore. We're past that phase." },
      { good: false, text: "I already told you I love you. Why do I need to keep saying it?" }
    ]
  }
};

const AchievementPanel = ({ relationshipId, escalationState, onOpenSlideout }) => {
  const { isCyber } = useTheme();
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (relationshipId) {
      fetchAchievements();
    }
  }, [relationshipId]);

  // Refresh achievements when escalation state changes (after each message)
  useEffect(() => {
    if (relationshipId && escalationState?.updated_at) {
      fetchAchievements();
    }
  }, [escalationState?.updated_at]);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(`/escalation/achievements/${relationshipId}`);
      setAchievements(response.data);
    } catch (error) {
      console.error("Error fetching achievements:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewAchievements = async () => {
    try {
      setChecking(true);
      const response = await authAxios.post(`/escalation/achievements/check/${relationshipId}`);
      // Always refresh achievements list to show updated progress
      await fetchAchievements();
      
      if (response.data.new_achievements?.length > 0) {
        toast.success(`🏆 New achievement${response.data.new_achievements.length > 1 ? 's' : ''} earned!`);
      } else {
        toast.info("Progress updated! Keep chatting to earn achievements.");
      }
    } catch (error) {
      console.error("Error checking achievements:", error);
      toast.error("Failed to check achievements");
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-divine-gold" />
      </div>
    );
  }

  if (!achievements) {
    return null;
  }

  const earnedCount = achievements.earned?.length || 0;
  const inProgressCount = achievements.in_progress?.length || 0;
  const totalCount = earnedCount + inProgressCount + (achievements.locked?.length || 0);

  const currentLevel = escalationState?.level || 1;
  const levelName = STAGE_LABELS[currentLevel] || "Unknown";
  const levelObjective = STAGE_OBJECTIVES[currentLevel] || "Continue building your connection.";

  return (
    <div className="border-t border-white/10 pt-1">
      {/* Main Header - Level Objective (Collapsed View) */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-0.5 text-white/80 hover:text-white transition-colors"
        data-testid="objective-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan-400" />
          <span className="text-xs font-medium">
            {levelName} Objective
          </span>
          <span className="text-xs text-white/40 ml-1">
            • {earnedCount}/{totalCount} 🏆
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {/* Expanded View */}
      {expanded && (
        <div className="mt-2 space-y-4">
          {/* Level Objective Description */}
          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-start justify-between gap-2">
              <p className="text-white/70 text-sm leading-relaxed flex-1">
                {levelObjective}
              </p>
              <button
                onClick={() => onOpenSlideout && onOpenSlideout()}
                className={`${isCyber ? 'text-cyan-400 hover:text-cyan-300' : 'text-divine-gold hover:text-divine-gold/80'} transition-colors flex-shrink-0 mt-0.5`}
                title="Open Session Coach"
                data-testid="objective-coach-btn"
              >
                <span className="text-base">🏹</span>
              </button>
            </div>
          </div>
          {/* Debug Panel - Real-time Game Values */}
          {escalationState && (
            <div className="p-2 bg-purple-900/30 rounded-lg border border-purple-500/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-purple-300 text-[10px] font-mono uppercase tracking-wider">🔧 Debug Values</span>
                {escalationState.processing && (
                  <span className="text-yellow-400 text-[10px] font-mono animate-pulse">⏳ Processing...</span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 text-[10px] font-mono">
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">Level</div>
                  <div className="text-white font-bold">{escalationState.level}</div>
                </div>
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">XP</div>
                  <div className={`font-bold ${escalationState.processing ? 'text-yellow-400 animate-pulse' : 'text-white'}`}>
                    {escalationState.xp?.toFixed(2)}
                  </div>
                </div>
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">Δ XP</div>
                  <div className={`font-bold ${escalationState.last_xp_delta > 0 ? 'text-green-400' : escalationState.last_xp_delta < 0 ? 'text-red-400' : 'text-white'}`}>
                    {escalationState.last_xp_delta > 0 ? '+' : ''}{escalationState.last_xp_delta?.toFixed(2)}
                  </div>
                </div>
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">Trend</div>
                  <div className={`font-bold ${escalationState.trend === 'up' ? 'text-green-400' : escalationState.trend === 'down' ? 'text-red-400' : 'text-yellow-400'}`}>
                    {escalationState.trend === 'up' ? '📈' : escalationState.trend === 'down' ? '📉' : '➡️'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px] font-mono mt-2">
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">Up Streak</div>
                  <div className="text-green-400 font-bold">{escalationState.trend_up_streak || 0}</div>
                </div>
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">Down Streak</div>
                  <div className="text-red-400 font-bold">{escalationState.trend_down_streak || 0}</div>
                </div>
                <div className="bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400">Confidence</div>
                  <div className="text-white font-bold">{(escalationState.stage_confidence * 100)?.toFixed(0)}%</div>
                </div>
              </div>
              {escalationState.last_reasons?.length > 0 && (
                <div className="mt-2 bg-black/30 p-1.5 rounded">
                  <div className="text-purple-400 text-[10px] font-mono mb-1">Last Signals</div>
                  <div className="flex flex-wrap gap-1">
                    {escalationState.last_reasons.map((reason, i) => (
                      <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded ${reason.startsWith('+') ? 'bg-green-500/20 text-green-300' : reason.startsWith('-') ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-white/70'}`}>
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Achievements Section */}
          <div className="border-t border-white/10 pt-3">
            <h3 className="text-xs text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5 text-divine-gold" />
              Achievements
              <span className="text-white/30">({earnedCount}/{totalCount})</span>
              {inProgressCount > 0 && (
                <span className="text-white/40">• {inProgressCount} in progress</span>
              )}
            </h3>

            {/* Earned Achievements */}
            {achievements.earned?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs text-white/60 mb-2 flex items-center gap-1">
                  <Trophy className="w-3 h-3 text-divine-gold" />
                  Earned
                </h4>
                <div className="flex flex-wrap gap-2">
                  {achievements.earned.map((a) => (
                    <div
                      key={a.achievement_id}
                      className="flex items-center gap-1.5 px-2 py-1 bg-divine-gold/20 border border-divine-gold/30 rounded-full"
                      title={a.description}
                      data-testid={`achievement-${a.achievement_id}`}
                    >
                      <span className="text-sm">{a.icon}</span>
                      <span className="text-xs text-divine-gold font-medium">{a.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* In Progress Achievements */}
            {achievements.in_progress?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs text-white/60 mb-2">In Progress</h4>
                <div className="space-y-1.5">
                  {achievements.in_progress.map((a) => (
                    <div
                      key={a.achievement_id}
                      className="flex items-center gap-2"
                      data-testid={`achievement-progress-${a.achievement_id}`}
                    >
                      <span className="text-sm opacity-60">{a.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-white/70">{a.name}</span>
                          <span className="text-[10px] text-white/50">
                            {Math.round(a.progress * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-1">
                          <div
                            className="bg-divine-gold/60 h-1 rounded-full transition-all"
                            style={{ width: `${a.progress * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Preview (show first 5) */}
            {achievements.locked?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs text-white/40 mb-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Locked ({achievements.locked.length})
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {achievements.locked.slice(0, 5).map((a) => (
                    <div
                      key={a.achievement_id}
                      className="flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded-full opacity-50"
                      title={a.description}
                    >
                      <span className="text-sm grayscale">{a.icon}</span>
                      <span className="text-xs text-white/40">{a.name}</span>
                    </div>
                  ))}
                  {achievements.locked.length > 5 && (
                    <span className="text-xs text-white/30 px-2 py-1">
                      +{achievements.locked.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Check for new achievements button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={checkForNewAchievements}
              disabled={checking}
              className="w-full text-xs text-white/60 hover:text-white hover:bg-white/10"
              data-testid="check-achievements-btn"
            >
              {checking ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <Trophy className="w-3 h-3 mr-1" />
                  Check for New Achievements
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Export constants for use in parent components
export { STAGE_LABELS, STAGE_OBJECTIVES, STAGE_DETAILED };
export default AchievementPanel;
